import { useEffect, useRef, useState } from "react";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { TreeClientListItem } from "../../clients/types/clients-api.types";
import { fetchVerifiedClientsTreeSearch } from "../utils/fetchVerifiedClientsTree";

const DEFAULT_DEBOUNCE_MS = 350;

type Result = {
  clients: TreeClientListItem[];
  isLoading: boolean;
  error: string | null;
  /** Texto ya debounced que disparó la última petición (alineado con `clients`). */
  debouncedSearch: string;
};

export function useDebouncedVerifiedClientSearch(
  enabled: boolean,
  searchInput: string,
  debounceMs: number = DEFAULT_DEBOUNCE_MS
): Result {
  const [debouncedInput, setDebouncedInput] = useState(searchInput);
  const [clients, setClients] = useState<TreeClientListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedInput(searchInput), debounceMs);
    return () => window.clearTimeout(t);
  }, [searchInput, debounceMs]);

  useEffect(() => {
    if (!enabled) {
      setClients([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const id = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    fetchVerifiedClientsTreeSearch(debouncedInput.trim() || undefined)
      .then((data) => {
        if (id !== requestIdRef.current) return;
        setClients(data);
      })
      .catch((err) => {
        if (id !== requestIdRef.current) return;
        const msg = HttpApiService.getErrorMessage(err);
        setError(msg || "Error al cargar clientes");
        setClients([]);
      })
      .finally(() => {
        if (id === requestIdRef.current) setIsLoading(false);
      });
  }, [enabled, debouncedInput]);

  return { clients, isLoading, error, debouncedSearch: debouncedInput };
}
